/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";
import { _cs } from "@togglecorp/fujs";

import connectWithStyles from "../../../utils/styles/connectWithStyles";

import styles from "./styles.module.scss";

interface SpinnerProps {
  className?: string;
  currentStyles: {
    [key: string]: any; // Match the Styles type from connectWithStyles
    fontSizeSuperLarge: string;
    fontSizeLarge: string;
    fontSizeSmallAlt: string;
  };
  updatedStyles: {
    [key: string]: any;
  };
  size?: "small" | "medium" | "large";
}

// Export this interface for external use (without currentStyles)
export interface ThemedSpinnerProps {
  className?: string;
  size?: "small" | "medium" | "large";
}

const sizeClassNameMap: {
  [key in NonNullable<SpinnerProps["size"]>]: string;
} = {
  small: styles.small,
  medium: styles.medium,
  large: styles.large,
};

function Spinner(props: SpinnerProps) {
  const { className, currentStyles, size = "medium" } = props;

  const { spinnerSize, centerX, centerY, radius, strokeWidth } = useMemo(() => {
    const sizeMapping: {
      [key in NonNullable<SpinnerProps["size"]>]: string;
    } = {
      small: currentStyles.fontSizeSmallAlt,
      medium: currentStyles.fontSizeLarge,
      large: currentStyles.fontSizeSuperLarge,
    };

    const strokeMapping: {
      [key in NonNullable<SpinnerProps["size"]>]: number;
    } = {
      small: 1,
      medium: 2,
      large: 3,
    };

    const spinnerStrokeWidth = strokeMapping[size];

    const spinnerSizeFromStyles = sizeMapping[size];

    const mySpinnerSize = spinnerSizeFromStyles.substring(
      0,
      spinnerSizeFromStyles.length - 2
    );
    const halfSpinnerSize = Math.floor(parseFloat(mySpinnerSize) / 2);

    return {
      spinnerSize: mySpinnerSize,
      centerX: halfSpinnerSize,
      centerY: halfSpinnerSize,
      radius: halfSpinnerSize - spinnerStrokeWidth,
      strokeWidth: spinnerStrokeWidth,
    };
  }, [size, currentStyles]);

  return (
    <div className={_cs(className, styles.spinner, sizeClassNameMap[size])}>
      <svg
        className={styles.svg}
        style={{
          width: `${spinnerSize}px`,
          height: `${spinnerSize}px`,
        }}
      >
        <circle
          className={styles.path}
          cx={centerX}
          cy={centerY}
          r={radius}
          strokeWidth={strokeWidth}
        />
      </svg>
    </div>
  );
}

const ThemedSpinner = connectWithStyles(Spinner);

export default ThemedSpinner as React.ComponentType<ThemedSpinnerProps>;
